import React, { useState, useEffect } from 'react';
import BottomSheet from '../components/BottomSheet/BottomSheet';
import Typography from '../components/Text/Typography';
import Tag from '../components/Tag/Tag';
import Chip from '../components/Chip/Chip';
import ProductCard from '../components/ProductCard/ProductCard';
import Icon from '../components/Icon/Icon';
import LoadingSpinner from '../components/LoadingSpinner';
import './ActiveDetailBottomSheet.css';

interface ActiveDetail {
  active_id: string;
  active_name: string;
  functions: string;
  concern_mapping: string;
  beginner_suggested_frequency: number;
  intermediate_suggested_frequency: number;
  advanced_suggested_frequency: number;
  time_of_day: 'AM' | 'PM' | 'Both';
  typical_percent_low: number;
  typical_percent_high: number;
  skin_compatibility: string;
  skin_incompatibility: string;
  irritation_risk: 'No' | 'Low' | 'Medium' | 'High';
  pregnancy_safe: 'No' | 'Yes';
  combinability: string;
  incombinability: string;
  user_level: 'beginner' | 'intermediate' | 'advanced';
  owned_products: Array<{
    product_id: number;
    product_name: string;
    brand: string;
    category: string;
    image_url: string;
    ingredients: string;
  }>;
}

interface ActiveDetailBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  activeId: string;
}

const ActiveDetailBottomSheet: React.FC<ActiveDetailBottomSheetProps> = ({
  isOpen,
  onClose,
  onBack,
  activeId
}) => {
  const [activeDetail, setActiveDetail] = useState<ActiveDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [originalLevel, setOriginalLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [saving, setSaving] = useState(false);

  // Fetch active detail data
  useEffect(() => {
    if (isOpen && activeId) {
      fetchActiveDetail();
    }
  }, [isOpen, activeId]);

  const fetchActiveDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/user/active-details/${activeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveDetail(data);
        setOriginalLevel(data.user_level);
        setSelectedLevel(data.user_level);
      } else {
        setError('Failed to fetch active ingredient details');
      }
    } catch (error) {
      console.error('Error fetching active detail:', error);
      setError('Error loading active ingredient details');
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'var(--color-primary-green)';
      case 'intermediate':
        return 'var(--color-warning-yellow)';
      case 'advanced':
        return 'var(--color-warning-red)';
      default:
        return 'var(--color-gray)';
    }
  };

  const getIrritationRiskColor = (risk: string) => {
    switch (risk) {
      case 'No':
        return 'var(--color-primary-green)';
      case 'Low':
        return 'var(--color-warning-yellow)';
      case 'Medium':
      case 'High':
        return 'var(--color-warning-red)';
      default:
        return 'var(--color-gray)';
    }
  };

  const handleSave = async () => {
    if (!activeDetail || selectedLevel === originalLevel) return;
    
    setSaving(true);
    try {
      const response = await fetch(`/api/user/active-levels/${activeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        },
        body: JSON.stringify({
          level: selectedLevel
        })
      });
      
      if (response.ok) {
        // Update the original level to reflect the saved state
        setOriginalLevel(selectedLevel);
        // Update the active detail with the new level
        setActiveDetail(prev => prev ? { ...prev, user_level: selectedLevel } : null);
      } else {
        setError('Failed to update active ingredient level');
      }
    } catch (error) {
      console.error('Error updating active ingredient level:', error);
      setError('Error updating active ingredient level');
    } finally {
      setSaving(false);
    }
  };

  // Check if there are any changes
  const hasChanges = selectedLevel !== originalLevel;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title=""
      height="90vh"
    >
      <div className="active-detail-bottom-sheet">
        {/* Header with back button and title */}
        <div className="active-detail-header">
          {onBack && (
            <button
              className="back-button"
              onClick={onBack}
              aria-label="Go back"
            >
              <Icon name="chevron-left" size="small" />
            </button>
          )}
          <div className="header-content">
            <Typography variant="h3" className="active-name">
              {activeDetail?.active_name || 'Loading...'}
            </Typography>
            {activeDetail && (
              <div className="header-tags">
                <div
                  className="level-tag"
                  style={{ backgroundColor: getLevelColor(activeDetail.user_level) }}
                >
                  <Typography variant="body-small" className="level-text">
                    {activeDetail.user_level.charAt(0).toUpperCase() + activeDetail.user_level.slice(1)}
                  </Typography>
                </div>
                {activeDetail.owned_products.length > 0 && (
                  <Tag>
                    Owned
                  </Tag>
                )}
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="loading-state">
            <LoadingSpinner size="small" text="Loading active ingredient details..." />
          </div>
        )}

        {error && (
          <div className="error-state">
            <Typography variant="body" className="error-text">
              {error}
            </Typography>
          </div>
        )}

        {activeDetail && !loading && (
          <div className="active-detail-content">
            {/* Functions Section */}
            <div className="detail-section">
              <Typography variant="h4" className="section-title">
                Functions
              </Typography>
              <Typography variant="body" className="section-content">
                {activeDetail.functions}
              </Typography>
            </div>

            {/* Usage Information */}
            <div className="detail-section">
              <Typography variant="h4" className="section-title">
                Usage Information
              </Typography>
              <div className="usage-grid">
                <div className="usage-item">
                  <Typography variant="body-small-bold">Time of Day</Typography>
                  <Typography variant="body">{activeDetail.time_of_day}</Typography>
                </div>
                <div className="usage-item">
                  <Typography variant="body-small-bold">Typical Concentration</Typography>
                  <Typography variant="body">
                    {activeDetail.typical_percent_low}% - {activeDetail.typical_percent_high}%
                  </Typography>
                </div>
                <div className="usage-item">
                  <Typography variant="body-small-bold">Suggested Frequency</Typography>
                  <Typography variant="body">
                    {activeDetail.user_level === 'beginner' && `${activeDetail.beginner_suggested_frequency} times/week`}
                    {activeDetail.user_level === 'intermediate' && `${activeDetail.intermediate_suggested_frequency} times/week`}
                    {activeDetail.user_level === 'advanced' && `${activeDetail.advanced_suggested_frequency} times/week`}
                  </Typography>
                </div>
                <div className="usage-item">
                  <Typography variant="body-small-bold">Irritation Risk</Typography>
                  <Typography 
                    variant="body" 
                    style={{ color: getIrritationRiskColor(activeDetail.irritation_risk) }}
                  >
                    {activeDetail.irritation_risk}
                  </Typography>
                </div>
                <div className="usage-item">
                  <Typography variant="body-small-bold">Pregnancy Safe</Typography>
                  <Typography variant="body">{activeDetail.pregnancy_safe}</Typography>
                </div>
              </div>
            </div>

            {/* Skin Compatibility */}
            <div className="detail-section">
              <Typography variant="h4" className="section-title">
                Skin Compatibility
              </Typography>
              <div className="compatibility-grid">
                {activeDetail.skin_compatibility && (
                  <div className="compatibility-item">
                    <Typography variant="body-small-bold">Compatible With</Typography>
                    <Typography variant="body">{activeDetail.skin_compatibility}</Typography>
                  </div>
                )}
                {activeDetail.skin_incompatibility && (
                  <div className="compatibility-item">
                    <Typography variant="body-small-bold">Avoid If You Have</Typography>
                    <Typography variant="body">{activeDetail.skin_incompatibility}</Typography>
                  </div>
                )}
              </div>
            </div>

            {/* Owned Products Section */}
            {activeDetail.owned_products.length > 0 && (
              <div className="detail-section">
                <Typography variant="h4" className="section-title">
                  Products You Own ({activeDetail.owned_products.length})
                </Typography>
                <div className="owned-products-grid">
                  {activeDetail.owned_products.map((product) => (
                    <div key={product.product_id} className="owned-product-item">
                      <ProductCard
                        productId={product.product_id}
                        company={product.brand}
                        productName={product.product_name}
                        imageSrc={product.image_url}
                        tag={product.category}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Combinability Information */}
            {(activeDetail.combinability || activeDetail.incombinability) && (
              <div className="detail-section">
                <Typography variant="h4" className="section-title">
                  Combinability
                </Typography>
                {activeDetail.combinability && (
                  <div className="combinability-item">
                    <Typography variant="body-small-bold">Works Well With</Typography>
                    <Typography variant="body">{activeDetail.combinability}</Typography>
                  </div>
                )}
                {activeDetail.incombinability && (
                  <div className="combinability-item">
                    <Typography variant="body-small-bold">Avoid Combining With</Typography>
                    <Typography variant="body">{activeDetail.incombinability}</Typography>
                  </div>
                )}
              </div>
            )}

            {/* Experience Level Selector */}
            <div className="experience-level-section">
              <div className="level-chips">
                {(['beginner', 'intermediate', 'advanced'] as const).map((level) => (
                  <Chip
                    key={level}
                    label={level.charAt(0).toUpperCase() + level.slice(1)}
                    isSelected={selectedLevel === level}
                    onClick={() => setSelectedLevel(level)}
                    className="level-chip"
                  />
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button 
                className="cancel-button" 
                onClick={onBack || onClose}
                disabled={saving}
              >
                <Typography variant="body-small-bold">Back</Typography>
              </button>
              <button 
                className="save-button"
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                <Typography variant="body-small-bold">
                  {saving ? "Saving..." : "Save"}
                </Typography>
              </button>
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  );
};

export default ActiveDetailBottomSheet;

